import { supabase } from './supabase.js'
import { storageService } from './storageService.js'

class ImageLibraryService {
  constructor() {
    console.log('🖼️ Image Library Service initialized')
  }

  /**
   * Add image to user's library
   * @param {Object} imageData - Image data to add
   * @returns {Promise<Object>} Added image record
   */
  async addToLibrary(imageData) {
    try {
      const {
        userId,
        filePath,
        fileUrl,
        fileName,
        fileSize,
        mimeType,
        width,
        height,
        sourceType = 'upload',
        aiGeneratedImageId = null,
        tags = [],
        description = '',
        altText = ''
      } = imageData

      console.log('📚 Adding image to library:', fileName)

      const { data, error } = await supabase
        .from('user_image_library')
        .insert([{
          user_id: userId,
          file_path: filePath,
          file_url: fileUrl,
          file_name: fileName,
          file_size: fileSize,
          mime_type: mimeType,
          width: width,
          height: height,
          source_type: sourceType,
          ai_generated_image_id: aiGeneratedImageId,
          tags: tags,
          description: description,
          alt_text: altText
        }])
        .select()
        .single()

      if (error) throw error

      console.log('✅ Image added to library:', data.id)
      return data
    } catch (error) {
      console.error('Error adding image to library:', error)
      throw error
    }
  }

  /**
   * Get user's image library with filtering and pagination
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Images and metadata
   */
  async getUserLibrary(options = {}) {
    try {
      const {
        userId,
        sourceType = null,
        searchTerm = '',
        sortBy = 'created_at',
        sortOrder = 'desc',
        limit = 20,
        offset = 0
      } = options

      let query = supabase
        .from('user_image_library')
        .select('*')
        .eq('user_id', userId)

      // Filter by source type
      if (sourceType) {
        query = query.eq('source_type', sourceType)
      }

      // Search functionality
      if (searchTerm) {
        query = query.or(`file_name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,tags.cs.{${searchTerm}}`)
      }

      // Sorting
      query = query.order(sortBy, { ascending: sortOrder === 'asc' })

      // Pagination
      query = query.range(offset, offset + limit - 1)

      const { data, error, count } = await query

      if (error) throw error

      // Get total count for pagination
      const { count: totalCount } = await supabase
        .from('user_image_library')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)

      return {
        images: data || [],
        total: totalCount || 0,
        hasMore: (offset + limit) < (totalCount || 0)
      }
    } catch (error) {
      console.error('Error getting user library:', error)
      throw error
    }
  }

  /**
   * Get recent images (most recently used or created)
   * @param {string} userId - User ID
   * @param {number} limit - Number of images to return
   * @returns {Promise<Array>} Recent images
   */
  async getRecentImages(userId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('user_image_library')
        .select('*')
        .eq('user_id', userId)
        .order('last_used_at', { ascending: false, nullsLast: true })
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error getting recent images:', error)
      throw error
    }
  }

  /**
   * Update image metadata
   * @param {string} imageId - Image ID
   * @param {string} userId - User ID for security
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} Updated image
   */
  async updateImage(imageId, userId, updates) {
    try {
      const { data, error } = await supabase
        .from('user_image_library')
        .update(updates)
        .eq('id', imageId)
        .eq('user_id', userId)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error updating image:', error)
      throw error
    }
  }

  /**
   * Delete image from library and storage
   * @param {string} imageId - Image ID
   * @param {string} userId - User ID for security
   * @returns {Promise<void>}
   */
  async deleteImage(imageId, userId) {
    try {
      // Get image data first
      const { data: image, error: fetchError } = await supabase
        .from('user_image_library')
        .select('file_path')
        .eq('id', imageId)
        .eq('user_id', userId)
        .single()

      if (fetchError) throw fetchError

      // Delete from storage
      await storageService.deleteFile(image.file_path)

      // Delete from library
      const { error: deleteError } = await supabase
        .from('user_image_library')
        .delete()
        .eq('id', imageId)
        .eq('user_id', userId)

      if (deleteError) throw deleteError

      console.log('🗑️ Image deleted from library:', imageId)
    } catch (error) {
      console.error('Error deleting image:', error)
      throw error
    }
  }

  /**
   * Mark image as used (updates usage count and last used timestamp)
   * @param {string} imageId - Image ID
   * @param {string} userId - User ID for security
   * @returns {Promise<void>}
   */
  async markAsUsed(imageId, userId = null) {
    try {
      // First get the current usage count
      const { data: currentData, error: fetchError } = await supabase
        .from('user_image_library')
        .select('usage_count')
        .eq('id', imageId)
        .single()

      if (fetchError) throw fetchError

      const currentCount = currentData?.usage_count || 0
      
      // Update with incremented count
      const updateQuery = supabase
        .from('user_image_library')
        .update({
          usage_count: currentCount + 1,
          last_used_at: new Date().toISOString()
        })
        .eq('id', imageId)

      // Only add user_id filter if provided
      if (userId) {
        updateQuery.eq('user_id', userId)
      }

      const { error } = await updateQuery

      if (error) throw error
      
      console.log('✅ Image marked as used:', imageId)
    } catch (error) {
      console.error('Error marking image as used:', error)
      // Don't throw - this is not critical
    }
  }

  /**
   * Upload new image to library
   * @param {string} userId - User ID
   * @param {File} file - File to upload
   * @param {Object} metadata - Additional metadata
   * @returns {Promise<Object>} Added image record
   */
  async uploadImage(userId, file, metadata = {}) {
    try {
      // Validate file
      const validation = storageService.validateFile(file)
      if (!validation.valid) {
        throw new Error(validation.errors.join(' '))
      }

      // Upload to storage
      const uploadResult = await storageService.uploadFile(userId, file, 'uploads')

      // Get image dimensions if possible
      const dimensions = await this.getImageDimensions(file)

      // Add to library
      const libraryData = {
        userId,
        filePath: uploadResult.path,
        fileUrl: uploadResult.url,
        fileName: uploadResult.name,
        fileSize: uploadResult.size,
        mimeType: uploadResult.type,
        width: dimensions.width,
        height: dimensions.height,
        sourceType: 'upload',
        ...metadata
      }

      return await this.addToLibrary(libraryData)
    } catch (error) {
      console.error('Error uploading image:', error)
      throw error
    }
  }

  /**
   * Get image dimensions from file
   * @param {File} file - Image file
   * @returns {Promise<Object>} Width and height
   */
  async getImageDimensions(file) {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        resolve({ width: null, height: null })
        return
      }

      const img = new Image()
      img.onload = () => {
        resolve({ width: img.width, height: img.height })
      }
      img.onerror = () => {
        resolve({ width: null, height: null })
      }
      img.src = URL.createObjectURL(file)
    })
  }

  /**
   * Save AI generated image to library
   * @param {Object} aiImageData - AI generated image data
   * @returns {Promise<Object>} Added image record
   */
  async saveAIGeneratedImage(aiImageData) {
    try {
      const {
        userId,
        projectId,
        imageUrl,
        width,
        height,
        prompt,
        type,
        aiGeneratedImageId
      } = aiImageData

      // Generate a filename based on the prompt and type
      const timestamp = Date.now()
      const fileName = `${type}_${timestamp}.jpg`
      const filePath = `${userId}/generated/${type}/${fileName}`

      // Try to get file size from the image URL
      let fileSize = 1024000 // Default to 1MB estimate if we can't get actual size
      try {
        const response = await fetch(imageUrl, { method: 'HEAD' })
        const contentLength = response.headers.get('content-length')
        if (contentLength) {
          fileSize = parseInt(contentLength, 10)
          console.log('📏 Got file size:', fileSize, 'bytes')
        }
      } catch (error) {
        console.warn('⚠️ Could not get file size from URL, using estimate:', fileSize)
      }

      // Add to library
      const libraryData = {
        userId,
        filePath: filePath,
        fileUrl: imageUrl,
        fileName: fileName,
        fileSize: fileSize,
        mimeType: 'image/jpeg',
        width: width,
        height: height,
        sourceType: 'ai_generated',
        aiGeneratedImageId: aiGeneratedImageId,
        description: prompt,
        altText: `AI generated ${type} image`,
        tags: ['ai-generated', type]
      }

      return await this.addToLibrary(libraryData)
    } catch (error) {
      console.error('Error saving AI generated image to library:', error)
      // Don't throw - this shouldn't break the main flow
      return null
    }
  }

  /**
   * Get user's images with filtering and pagination
   * @param {string} userId - User ID
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Images and pagination info
   */
  async getUserImages(userId, options = {}) {
    try {
      console.log('🔍 Loading images for user:', userId)
      console.log('🔍 Query options:', options)

      const {
        search = '',
        filter = 'all',
        sortBy = 'created_at',
        sortOrder = 'desc',
        limit = 20,
        offset = 0
      } = options

      let query = supabase
        .from('user_image_library')
        .select('*')
        .eq('user_id', userId)

      // Apply filters
      if (filter !== 'all') {
        if (filter === 'recent') {
          // Get recent images (last 30 days)
          const thirtyDaysAgo = new Date()
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
          query = query.gte('created_at', thirtyDaysAgo.toISOString())
        } else {
          query = query.eq('source_type', filter)
        }
      }

      // Apply search
      if (search) {
        query = query.or(`file_name.ilike.%${search}%,description.ilike.%${search}%,tags.cs.{${search}}`)
      }

      // Apply sorting
      query = query.order(sortBy, { ascending: sortOrder === 'asc' })

      // Apply pagination
      query = query.range(offset, offset + limit - 1)

      console.log('🔍 Executing query...')
      const { data, error } = await query

      if (error) {
        console.error('❌ Query error:', error)
        throw error
      }

      console.log('✅ Query successful, found images:', data?.length || 0)
      console.log('📊 Sample images:', data?.slice(0, 2))

      return {
        images: data || [],
        hasMore: (data || []).length === limit
      }
    } catch (error) {
      console.error('Error getting user images:', error)
      throw error
    }
  }

  /**
   * Get user statistics
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User statistics
   */
  async getUserStats(userId) {
    try {
      console.log('📊 Loading stats for user:', userId)
      
      const { data, error } = await supabase
        .from('user_image_library')
        .select('source_type, file_size, created_at')
        .eq('user_id', userId)

      if (error) {
        console.error('❌ Stats query error:', error)
        throw error
      }

      console.log('📊 Stats query successful, found records:', data?.length || 0)

      const now = new Date()
      const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000))

      const stats = {
        total: data.length,
        uploaded: data.filter(img => img.source_type === 'upload').length,
        aiGenerated: data.filter(img => img.source_type === 'ai_generated').length,
        stock: data.filter(img => img.source_type === 'stock').length,
        recent: data.filter(img => new Date(img.created_at) >= thirtyDaysAgo).length,
        totalSize: data.reduce((sum, img) => sum + (img.file_size || 0), 0)
      }

      console.log('📊 Calculated stats:', stats)
      return stats
    } catch (error) {
      console.error('Error getting user stats:', error)
      throw error
    }
  }

  /**
   * Get library statistics (alias for getUserStats for backward compatibility)
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Library statistics
   */
  async getLibraryStats(userId) {
    return this.getUserStats(userId)
  }
}

// Create and export singleton instance
export const imageLibraryService = new ImageLibraryService()
